import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Input,
  Select,
  DropdownMenu,
  Modal,
  Toast,
  useStateContext,
} from '@veda/dashboard-ui';
import {
  MdBusiness, MdEdit, MdEmail, MdClose, MdArrowBack,
  MdPersonRemove, MdCheckCircle, MdPending,
} from 'react-icons/md';
import { usePppApi } from '../services/pppApi';
import { useAuthUser } from '../hooks/useAuthUser';

const RUOLO_OPTIONS = [
  { value: 'admin',  label: 'Amministratore' },
  { value: 'membro', label: 'Membro' },
];

const RUOLO_LABEL = { admin: 'Amministratore', membro: 'Membro' };

function Badge({ children, color = 'gray' }) {
  const colors = {
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    blue:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    gray:  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[color]}`}>{children}</span>;
}

// ─── Form modifica dati company (componenti Veda) ─────────────────────────────
function FormCompany({ iniziale, onSalva, onAnnulla, saving }) {
  const [form, setForm] = useState({
    ragioneSociale:       iniziale?.ragioneSociale       ?? '',
    pec:                  iniziale?.pec                  ?? '',
    email:                iniziale?.email                ?? '',
    telefono:             iniziale?.telefono             ?? '',
    formaGiuridica:       iniziale?.formaGiuridica       ?? '',
    rappresentanteLegale: iniziale?.rappresentanteLegale ?? '',
    indirizzo:            iniziale?.indirizzo            ?? '',
    cap:                  iniziale?.cap                  ?? '',
    comune:               iniziale?.comune               ?? '',
    provincia:            iniziale?.provincia            ?? '',
  });

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  return (
    <div className="max-w-4xl space-y-4">
      <Input label="Ragione sociale *" value={form.ragioneSociale} onChange={set('ragioneSociale')} />
      <div className="grid grid-cols-2 gap-4">
        <Input label="Forma giuridica" value={form.formaGiuridica} onChange={set('formaGiuridica')} />
        <Input label="Rappresentante legale" value={form.rappresentanteLegale} onChange={set('rappresentanteLegale')} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input label="PEC" type="email" value={form.pec} onChange={set('pec')} />
        <Input label="Email" type="email" value={form.email} onChange={set('email')} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input label="Telefono" value={form.telefono} onChange={set('telefono')} />
        <Input label="Indirizzo" value={form.indirizzo} onChange={set('indirizzo')} />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Input label="Comune" value={form.comune} onChange={set('comune')} />
        <Input label="Provincia" value={form.provincia} onChange={set('provincia')} />
        <Input label="CAP" value={form.cap} onChange={set('cap')} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onAnnulla}
          className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Annulla
        </button>
        <button
          onClick={() => onSalva(form)}
          disabled={saving || !form.ragioneSociale.trim()}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white transition-colors"
        >
          {saving ? 'Salvataggio...' : 'Salva modifiche'}
        </button>
      </div>
    </div>
  );
}

// ─── Pannello account/membri di una company ───────────────────────────────────
function PannelloAccount({ company, canInvita, canRimuovi }) {
  const api = usePppApi();
  const { addToast, addModal } = useStateContext();
  const [membri,   setMembri]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [email,    setEmail]    = useState('');
  const [ruolo,    setRuolo]    = useState('membro');
  const [inviando, setInviando] = useState(false);
  const [msg,      setMsg]      = useState('');

  const carica = useCallback(() => {
    setLoading(true);
    api.listMembriCompany(company.id, {
      onSuccess: (data) => { setMembri(Array.isArray(data) ? data : []); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  useEffect(() => { carica(); }, [carica]);

  const handleInvita = () => {
    if (!email.trim()) return;
    setInviando(true);
    api.invitaMembroCompany(company.id, { email, ruolo }, {
      onSuccess: () => {
        setEmail('');
        setMsg(`Invito inviato a ${email}`);
        setInviando(false);
        setTimeout(() => setMsg(''), 4000);
        carica();
      },
      onFormError: (err) => {
        setInviando(false);
        setMsg(err?.errors?.email?.[0] ?? err?.message ?? 'Errore durante l\'invio.');
      },
    });
  };

  const handleRevoca = (row) => {
    addModal(
      <Modal
        title="Revoca invito"
        content={`Revocare l'invito per ${row.email}?`}
        confirm="Revoca"
        style="danger"
        onClick={() => api.revocaInvitoCompany(company.id, row.invitoId ?? row.id, {
          onSuccess: () => { addToast(<Toast duration={3} content="Invito revocato." />); carica(); },
          onFormError: (err) => addToast(<Toast duration={4} content={err?.message ?? 'Errore.'} />),
        })}
      />
    );
  };

  const handleRimuovi = (row) => {
    addModal(
      <Modal
        title="Rimuovi account"
        content={`Rimuovere ${row.nome} dalla company? L'account non sarà più associato.`}
        confirm="Rimuovi"
        style="danger"
        onClick={() => api.rimuoviMembroCompany(company.id, row.operatoreId, {
          onSuccess: () => { addToast(<Toast duration={3} content="Account rimosso." />); carica(); },
          onFormError: (err) => addToast(<Toast duration={4} content={err?.errors?.membro?.[0] ?? err?.message ?? 'Errore.'} />),
        })}
      />
    );
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 mt-6 pt-6 max-w-4xl">
      <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-4">
        <MdEmail size={18} className="text-blue-500" />
        Account di {company.ragioneSociale}
      </h3>

      {canInvita && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleInvita()}
            placeholder="email@operatore.it"
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="sm:w-56">
            <Select label="Ruolo" value={ruolo} onChange={(e) => setRuolo(e.target.value)} options={RUOLO_OPTIONS} />
          </div>
          <button
            onClick={handleInvita}
            disabled={inviando || !email.trim()}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white transition-colors"
          >
            <MdEmail size={16} /> {inviando ? 'Invio...' : 'Invita'}
          </button>
        </div>
      )}

      {msg && (
        <p className="text-sm text-green-600 dark:text-green-400 mb-3 flex items-center gap-1">
          <MdCheckCircle size={16} /> {msg}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 py-4 text-center">Caricamento...</p>
      ) : (
        <Table
          cols={[
            { name: 'nome',   label: 'Nome' },
            { name: 'email',  label: 'Email' },
            { name: 'ruolo',  label: 'Ruolo',  size: 'w-40' },
            { name: 'stato',  label: 'Stato',  size: 'w-32' },
            { name: 'azioni', label: '',       size: 'w-10', orderable: 0 },
          ]}
          cells_style={{
            email: (row) => row.email ?? <span className="text-gray-400">—</span>,
            ruolo: (row) => <Badge color="blue">{RUOLO_LABEL[row.ruolo] ?? row.ruolo}</Badge>,
            stato: (row) => row.stato === 'invitato'
              ? <Badge color="amber"><MdPending size={12} /> In attesa</Badge>
              : <Badge color="green">Attivo</Badge>,
            azioni: (row) => {
              if (row.stato === 'invitato') {
                if (!canInvita) return null;
                return (
                  <DropdownMenu menu_items={[
                    <button key="revoca" onClick={() => handleRevoca(row)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                      <MdClose size={16} /> Revoca invito
                    </button>,
                  ]} />
                );
              }
              if (!canRimuovi) return null;
              return (
                <DropdownMenu menu_items={[
                  <button key="rimuovi" onClick={() => handleRimuovi(row)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                    <MdPersonRemove size={16} /> Rimuovi account
                  </button>,
                ]} />
              );
            },
          }}
          dataset={membri}
        />
      )}
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function CompanyAdminList() {
  const api = usePppApi();
  const { user, can } = useAuthUser();
  const navigate = useNavigate();
  const { addToast } = useStateContext();

  // L'operatore economico non gestisce le altre company — va al proprio portale
  useEffect(() => {
    if (user?.role === 'operatore_economico') {
      navigate('/operatore/company', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const [companies,  setCompanies]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [selezionata, setSelezionata] = useState(null); // company aperta (dettaglio)

  const carica = useCallback(() => {
    setLoading(true);
    api.listCompanies({
      onSuccess: (data) => { setCompanies(Array.isArray(data) ? data : []); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const apriDettaglio = (company) => setSelezionata(company);

  const handleAggiorna = (form) => {
    setSaving(true);
    api.aggiornaCompany(selezionata.id, form, {
      onSuccess: () => {
        setSaving(false);
        addToast(<Toast duration={4} content="Company aggiornata." />);
        const aggiornata = { ...selezionata, ...form };
        setSelezionata(aggiornata);
        carica();
      },
      onFormError: () => setSaving(false),
    });
  };

  const canEdit    = can('ppp.companies.edit');
  const canInvita  = can('ppp.companies.invite');
  const canRimuovi = can('ppp.companies.remove_member');

  const cols = [
    { name: 'action',         label: '',               size: 'w-10 max-w-[40px]', orderable: 0 },
    { name: 'ragioneSociale', label: 'Ragione sociale' },
    { name: 'identificativi', label: 'P.IVA / C.F.',   size: 'w-48' },
    { name: 'comune',         label: 'Sede',           size: 'w-40' },
    { name: 'pec',            label: 'PEC',            size: 'w-56' },
    { name: 'numeroMembri',   label: 'Account',        size: 'w-24' },
  ];

  const menuItems = (company) => [
    <button
      key="dettaglio"
      onClick={() => apriDettaglio(company)}
      className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
    >
      <MdEdit size={16} /> Dettaglio e account
    </button>,
  ];

  const cells = {
    action: (row) => <DropdownMenu menu_items={menuItems(row)} />,
    ragioneSociale: (row) => (
      <div className="flex items-center gap-2">
        <MdBusiness size={16} className="text-blue-400 shrink-0" />
        <button
          onClick={() => apriDettaglio(row)}
          title="Apri dettaglio e account"
          className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors text-left"
        >
          {row.ragioneSociale}
        </button>
      </div>
    ),
    identificativi: (row) => (
      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
        {row.partitaIva || row.codiceFiscale || '—'}
      </span>
    ),
    comune: (row) => [row.comune, row.provincia ? `(${row.provincia})` : null].filter(Boolean).join(' ') || '—',
    pec: (row) => <span className="text-xs text-gray-500 dark:text-gray-400">{row.pec ?? '—'}</span>,
    numeroMembri: (row) => <Badge color="gray">{row.numeroMembri ?? 0}</Badge>,
  };

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 lg:p-10">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <MdBusiness size={22} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Operatori Economici</h1>
              <p className="text-sm text-gray-500 mt-0.5">Tutte le aziende registrate e i relativi account</p>
            </div>
          </div>
          {selezionata && (
            <button
              onClick={() => setSelezionata(null)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <MdArrowBack size={16} /> Torna alla lista
            </button>
          )}
        </div>

        {selezionata ? (
          /* Dettaglio company */
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <MdBusiness size={18} className="text-blue-500" />
              {selezionata.ragioneSociale}
            </h2>

            {canEdit ? (
              <FormCompany
                iniziale={selezionata}
                onSalva={handleAggiorna}
                onAnnulla={() => setSelezionata(null)}
                saving={saving}
              />
            ) : (
              <p className="text-sm text-gray-500 max-w-4xl">
                Non hai i permessi per modificare i dati di questa company.
              </p>
            )}

            <PannelloAccount
              company={selezionata}
              canInvita={canInvita}
              canRimuovi={canRimuovi}
            />
          </div>
        ) : (
          /* Lista company */
          <Table
            cols={cols}
            dataset={companies}
            cells_style={cells}
            loading={loading}
            empty_message="Nessuna company registrata."
          />
        )}

      </div>
    </div>
  );
}
